<template>
    <div class="text-left">
        <div v-if="hasPagination" class="d-block mb-1">
            <AppSearchInput :value="searchQuery" @input="fetchOptions($event.target.value)"/>
        </div>
        <ag-grid-vue style="width: 100%; height: 148px;"
                     class="ag-theme-balham"
                     :columnDefs="settings.columnDefs"
                     :rowData="rowsData"
                     :defaultColDef="defaultColDef"
                     :enableCellChangeFlash="true"
                     :suppressCellSelection="false"
                     :suppressCopyRowsToClipboard="true"
                     rowSelection="single"
                     :navigateToNextCell="navigateToNextCell"
                     :tooltipShowDelay="500"
                     @grid-ready="onGridReady"
                     @selection-changed="onSelectionChanged"
                     @cell-value-changed="onCellValueChanged">
        </ag-grid-vue>
        <div v-if="error" class="alert-danger rounded my-1">
            <tree-view :data="error" :options="{ maxDepth: 1, rootObjectKey: 'Error' }"/>
        </div>
        <div v-if="!readonlyTable" class="row py-2">
            <div @click="addRow" v-loading.btn="isRowAdding" class="col-4 text-purple clickable">
                Add
            </div>
            <div v-if="selectedRows && selectedRows.length !== 0"
                 v-loading.btn="isRemovingSelected"
                 @click="removeSelected"
                 class="col-8 text-danger clickable text-right">
                Remove selected
            </div>
        </div>
    </div>
</template>

<script>
import {
    checkFieldSelectOptionsHasPagination,
    FieldEditorOptionsSources,
    getApiSelectQueryStringParamName,
    getFormFieldOptions
} from '../../../../../../../../types/document-viewer/field/modules/editor/select'
import AppSearchInput from '../../../../../../../../components/shared/AppSearchInput'
import axios from 'axios'
import {getFormFieldFormattedLabel} from '../../../../../../../../types/document-viewer/field/modules/editor'

export default {
    name: 'FieldOptionsTable',
    components: {AppSearchInput},
    props: {
        field: {
            type: Object
        }
    },
    data() {
        return {
            gridApi: null,
            columnApi: null,
            selectedRows: [],

            settings: {
                columnDefs: []
            },

            rowsData: [],

            defaultColDef: null,

            isRowsFetching: false,
            isRowAdding: false,
            isRemovingSelected: false,
            searchQuery: '',
            error: null,
            FieldEditorOptionsSources
        }
    },
    computed: {
        editor() {
            return this.field.editor || {}
        },

        select() {
            return this.editor.select || {}
        },

        readonlyTable() {
            return [FieldEditorOptionsSources.API, FieldEditorOptionsSources.STORAGE, FieldEditorOptionsSources.DATASET].includes(this.select.source)
        },

        hasPagination() {
            return checkFieldSelectOptionsHasPagination(this.select)
        },

        queryStringParamName() {
            return getApiSelectQueryStringParamName(this.select)
        }
    },
    methods: {
        onGridReady(params) {
            this.gridApi = params.api
            this.columnApi = params.columnApi

            this.fetchOptions()
        },

        setupColumnDefs() {
            const columnDefs = [{
                field: this.select.trackBy,
                headerName: this.select.trackBy,
                tooltipField: this.select.trackBy,
                tooltipValueGetter: ({value}) => value
            }]

            if (this.readonlyTable) {
                columnDefs.push({
                    headerName: this.select.label,
                    valueFormatter: ({data}) => getFormFieldFormattedLabel({editor: this.editor, value: data}),
                    tooltipValueGetter: (p) => p.valueFormatted
                })
            } else {
                columnDefs.push({
                    field: this.select.label,
                    headerName: this.select.label,
                    tooltipField: this.select.label,
                    tooltipValueGetter: ({value}) => value
                })
            }

            if (this.gridApi) {
                this.gridApi.setColumnDefs(columnDefs)
            } else {
                this.settings.columnDefs = columnDefs
            }
        },

        addRow() {
            this.isRowAdding = true
            const newRow = {}
            this.settings.columnDefs.forEach(c => {
                newRow[c.field] = ''
            })
            this.gridApi.applyTransaction({add: [newRow]})
            this.isRowAdding = false
        },

        removeSelected() {
            this.isRemovingSelected = true
            this.gridApi.showLoadingOverlay()
            this.gridApi.applyTransaction({remove: this.selectedRows})
            this.gridApi.hideOverlay()
            this.isRemovingSelected = false
        },

        onSelectionChanged() {
            this.selectedRows = this.gridApi.getSelectedRows()
        },

        onCellValueChanged(e) {
            this.$emit('update', this.getRowsData())
        },

        getRowsData() {
            const rowsData = []
            this.gridApi.forEachNode(function(node) {
                rowsData.push(node.data)
            })
            return rowsData
        },

        async fetchOptions(query) {
            if (!this.gridApi) {
                return
            }
            this.isRowsFetching = true
            const params = {}
            if (this.hasPagination) {
                params[this.queryStringParamName] = query
            }
            if (this.field.dependants) {
                params.dependencies = this.field.dependants
            }
            let data = []
            this.error = null
            try {
                ({data} = await getFormFieldOptions({
                    select: this.select,
                    requestId: `${this.field.path}_options_table`,
                    params
                }))
                this.isRowsFetching = false
            } catch (e) {
                if (!axios.isCancel(e)) {
                    this.isRowsFetching = false
                }
                if (e && e.response && e.response.data) {
                    this.error = e.response.data
                }
            }
            if (!this.isRowsFetching) { // there can be canceled request and setRowData terminates ag-grig loading overlay
                this.gridApi.setRowData(data)
            }
        },

        onSettingsUpdate(settings) {
            this.settings = _.cloneDeep(settings)
            this.gridApi.setColumnDefs(this.settings.columnDefs)
        },

        onRowsDataUpdate(rowsData) {
            this.gridApi.applyTransaction({update: rowsData})
        },

        navigateToNextCell(params) {
            const suggestedNextCell = params.nextCellPosition

            const KEY_UP = 38
            const KEY_DOWN = 40

            const noUpOrDownKeyPressed = params.key !== KEY_DOWN && params.key !== KEY_UP
            if (noUpOrDownKeyPressed) {
                return suggestedNextCell
            }

            this.gridApi.forEachNode(node => {
                if (node && suggestedNextCell && node.rowIndex === suggestedNextCell.rowIndex) {
                    node.setSelected(true)
                }
            })

            return suggestedNextCell
        }
    },
    created() {
        this.defaultColDef = {
            flex: 1,
            editable: !this.readonlyTable,
            sortable: true,
            filter: true
        }
        this.$watch(vm => [
            vm.select.source,
            vm.select.trackBy,
            vm.select.label,
            vm.select.storage,
            vm.select.dataset,
            vm.select.api
        ], () => {
            this.setupColumnDefs()
            this.fetchOptions()
        }, {deep: true, immediate: true})
    },
    watch: {
        isRowsFetching(v) {
            if (!this.gridApi) {
                return
            }
            if (v) {
                this.gridApi.showLoadingOverlay()
            } else {
                this.gridApi.hideOverlay()
            }
        }
    }
}
</script>
